import {
  BorderRadiusCorners,
  Box,
  Button,
  Divider,
  Icon,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import PlannedBreaksPicker from 'modules/shift_management/components/ShiftManagement/PlannedBreaksPicker'
import ShiftAccessSelectors from 'modules/shift_management/components/ShiftManagement/ShiftAccessSelectors'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import ShiftTimePicker from 'modules/shift_management/components/ShiftManagement/ShiftTimePicker'
import UnsafeRadioInput from 'modules/shift_management/components/ShiftManagement/UnsafeRadioInput'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

interface Props {
  form: UnassignedShiftForm
  onSelectNewFromScratch: () => void
  isSelected: boolean
  fromUnassigned?: boolean
  handleOpenBreaksModal?: () => void
  plannedBreakForms?: PlannedBreakForm[]
  onDeleteBreak?: (breakForm: PlannedBreakForm) => void
  isAutomationEnabled?: boolean
  locationFilterValue: Field
  workAreaFilterValue: Field
}

const NewShiftForm = ({
  form,
  onSelectNewFromScratch,
  isSelected,
  fromUnassigned,
  handleOpenBreaksModal,
  plannedBreakForms,
  onDeleteBreak,
  isAutomationEnabled,
  locationFilterValue,
  workAreaFilterValue,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const showLocation = !hasSelection || !view.isByAreas

  const [hideCallout, setHideCallout] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHideSaveNewShiftInfo,
    false
  )

  if (isAutomationEnabled) {
    const locationFormField = form.get('location_id')
    const workAreaFormField = form.get('work_area_id')
    locationFormField.set(locationFilterValue)
    workAreaFormField.set(workAreaFilterValue)
  }

  const borderColor = isSelected ? 'viridian1000' : 'grey400'
  const borderRadius: BorderRadiusCorners = {
    all: 'abs008',
  }

  const hasBreaks = hasFeatureFlag(ShiftManagementFeatureFlags.TIME_SETTINGS_BREAKS)

  const startTimeField = fromUnassigned ? form.get('start_at') : form.get('start_time')
  const endTimeField = fromUnassigned ? form.get('end_at') : form.get('end_time')
  return (
    <Box gap="s8">
      <Text.Normal size="300" weight="medium" color="grey1000">
        {i18n.t('shift_management.add_shift.create_a_new_shift')}:
      </Text.Normal>

      <Box background="grey200" borderRadius={borderRadius}>
        <Box
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: borderColor,
            },
          }}
          borderRadius={borderRadius}
          background="grey000White"
        >
          <Box
            alignItems="center"
            borderRadius={borderRadius}
            padding="s16"
            flexDirection="row"
            justifyContent="spaceBetween"
            background="grey000White"
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s4">
              <Icon.Small icon="circle" color="grey400" />
              <Text size="300" weight="semibold" color="grey800">
                {i18n.t('shift_management.add_shift.from_scratch')}
              </Text>
            </Box>

            <Touchable onClick={onSelectNewFromScratch}>
              <UnsafeRadioInput selected={isSelected} />
            </Touchable>
          </Box>

          {isSelected && (
            <>
              <Box paddingX="s16">
                <Divider />
              </Box>
              <Box gap="s16" borderRadius={borderRadius} padding="s16">
                {!hideCallout && (
                  <Callout
                    type="info"
                    description={i18n.t('shift_management.add_shift.from_scratch_description_new')}
                    onRemove={() => setHideCallout(true)}
                  />
                )}

                <ShiftTimePicker startField={startTimeField} endField={endTimeField} />
                <ShiftOvertimeCheckbox form={form} />
                {hasBreaks && !fromUnassigned && (
                  <Box gap={'s16'}>
                    <Divider />
                    {!!plannedBreakForms?.length && onDeleteBreak && (
                      <PlannedBreaksPicker
                        validationParams={{
                          startAt: form.startAt,
                          endAt: form.endAt,
                        }}
                        plannedBreakForms={plannedBreakForms}
                        onDelete={onDeleteBreak}
                      />
                    )}

                    <Box flexDirection="row">
                      <Button type="secondary" onClick={handleOpenBreaksModal} iconName="add">
                        {i18n.t('shift_management.saved_shifts.actions.add.break')}
                      </Button>
                    </Box>

                    <Divider />
                  </Box>
                )}
                {!isAutomationEnabled && (
                  <>
                    {showLocation && (
                      <ShiftLocationPicker
                        locationField={form.get('location_id')}
                        workAreaField={form.get('work_area_id')}
                      />
                    )}
                    <ShiftNotesField notesField={form.get('notes')} />
                  </>
                )}

                <ShiftAccessSelectors form={form} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default observer(NewShiftForm)
